import * as React from 'react';
import {connect} from 'react-redux';

import get from 'lodash/get';
import set from 'lodash/set';
import unset from 'lodash/unset';
import * as ramda from 'ramda';

import {ActivityTypes} from 'nutshell-core/activity-types';
import {Assignables} from 'nutshell-core/assignables';
import {Routing} from 'nutshell-core/routing';
import * as Schema from 'nutshell-core/schema';
import {Session} from 'nutshell-core/session';
import type {ActivityType, Assignable, FilterObject, User} from 'nutshell-core/types';

import {CheckboxButton} from 'shells/button/checkbox-button/checkbox-button';
import {convertToModels} from 'shells/filter-capsule/convert-to-models';
import {FilterCapsule} from 'shells/filter-capsule/filter-capsule';
import {VIEW_ACTIVITIES_TOUR_FILTERS_ID} from 'shells/tours/tours';
import {useNutshellTour} from 'shells/tours/use-nutshell-tour';

import type {NutshellState} from '../../../../store';

// @ts-expect-error
import * as actions from '../../dashboard-actions';
// @ts-expect-error
import * as dashboardSelectors from '../../dashboard-selectors';

import * as activitiesSelectors from './activities-selectors';

import styles from './activity-filters.module.css';

const mapStateToProps = (state: NutshellState): StateProps => {
    return {
        canUseFilters: activitiesSelectors.getCanUseFilters(state),
        schema: dashboardSelectors.getDashboardSchema(state),
        hasImportantActivities: activitiesSelectors.getHasImportantActivities(state),
        loggedInUser: Session.getSessionStateUser(state),
        activityTypes: ActivityTypes.getAll(state),
        assignables: Assignables.getAll(state),
    };
};

// @ts-expect-error
const mapDispatchToProps = (dispatch): DispatchProps => {
    return {
        onFilterUpdate: (formValues) => {
            dispatch(actions.updateActivityFilter(formValues));
        },
    };
};

type OwnProps = {
    filter: any;
};
type StateProps = {
    canUseFilters: boolean;
    schema: Schema.Properties;
    hasImportantActivities: boolean;
    loggedInUser: User | null | undefined;
    activityTypes: ActivityType[];
    assignables: Assignable[];
};
type DispatchProps = {
    onFilterUpdate: (newFilters: string) => any;
};

type Props = OwnProps & StateProps & DispatchProps;

type FilterKey = 'activityType' | 'participant' | 'status' | 'isImportant' | 'leadPriority';

// This should be a hook, but this component needs to be converted to a
// functional component first.
function ActivityStatusTourWatcher({status}: {status: any}) {
    const {progressTour} = useNutshellTour();
    const hasProgressed = React.useRef(false);

    React.useEffect(() => {
        if (hasProgressed.current) return;

        const statusValue = typeof status === 'string' ? status : status?.data?.[0]?.data;

        if (statusValue === 'scheduled') {
            progressTour();
            hasProgressed.current = true;
        }
    }, [status, progressTour]);

    return null;
}

export class ActivityFiltersComponent extends React.Component<Props> {
    render() {
        const {filter, loggedInUser} = this.props;
        const isImportantChecked = get(filter, 'isImportant') === 'true';
        const isForHotLeadsChecked = get(filter, 'leadPriority') !== undefined;

        return (
            <div
                id={VIEW_ACTIVITIES_TOUR_FILTERS_ID}
                className={
                    this.props.canUseFilters ? styles.container : styles['container-requesting']
                }
            >
                <ActivityStatusTourWatcher status={get(filter, 'status')} />
                <FilterCapsule
                    hasSearch={true}
                    key='activityType'
                    collection={convertToModels(this.props.activityTypes)}
                    filter={get(filter, 'activityType')}
                    filterName='activityType'
                    schemaField={this.props.schema.activityType}
                    onSubmit={this.handleFilterSubmit}
                    // @ts-expect-error
                    onUpdate={this.handleFilterSubmit}
                    onRemove={this.handleFormFilterRemove}
                />
                {loggedInUser && !loggedInUser.isViewingRestricted ? (
                    <FilterCapsule
                        hasSearch={true}
                        key='participant'
                        collection={convertToModels(this.props.assignables)}
                        filter={get(filter, 'participant')}
                        filterName='participant'
                        schemaField={this.props.schema.participant}
                        defaultFilter={{
                            anyAll: 'any',
                            data: [{data: loggedInUser.id}],
                        }}
                        onSubmit={this.handleFilterSubmit}
                        // @ts-expect-error
                        onUpdate={this.handleFilterSubmit}
                        onRemove={this.handleFormFilterRemove}
                    />
                ) : null}
                <FilterCapsule
                    collection={convertToModels(
                        this.props.schema.status &&
                            // @ts-expect-error
                            this.props.schema.status.enum &&
                            // @ts-expect-error
                            Array.isArray(this.props.schema.status.enum)
                            ? // @ts-expect-error
                              this.props.schema.status.enum
                            : undefined
                    )}
                    filter={get(filter, 'status')}
                    filterName='status'
                    defaultValueTitle='Any status'
                    schemaField={this.props.schema.status}
                    onSubmit={this.handleFilterSubmit}
                    // @ts-expect-error
                    onUpdate={this.handleFilterSubmit}
                    onRemove={this.handleFormFilterRemove}
                    popoverLocation='bottom'
                />
                {this.props.hasImportantActivities ? (
                    <CheckboxButton
                        label='Only important'
                        checked={isImportantChecked}
                        size='small'
                        onChange={(isChecked) =>
                            this.handleFormFilterChange('isImportant', isChecked)
                        }
                    />
                ) : undefined}
                {NutClientConfig.hasHotLeads ? (
                    <CheckboxButton
                        label='Only hot leads'
                        checked={isForHotLeadsChecked}
                        size='small'
                        onChange={(isChecked) =>
                            this.handleFormFilterChange(
                                'leadPriority',
                                isChecked ? ['hot'] : undefined
                            )
                        }
                    />
                ) : undefined}
            </div>
        );
    }

    handleFilterSubmit = (data: FilterObject, key: FilterKey) => {
        const filterName = Object.keys(data)[0];
        // @ts-expect-error
        this.handleFormFilterChange(key, data[filterName]);
    };

    handleFormFilterChange = (
        key: FilterKey,
        newValue: FilterObject | boolean | string[] | null | undefined
    ) => {
        const deparamedCurrentValue = ramda.clone(this.props.filter);

        set(deparamedCurrentValue, key, newValue);
        const paramedNewValue = Routing.param(deparamedCurrentValue);

        this.props.onFilterUpdate(paramedNewValue);
    };

    handleFormFilterRemove = (key: FilterKey) => {
        const deparamedCurrentValue = ramda.clone(this.props.filter);
        unset(deparamedCurrentValue, key);
        const paramedNewValue = Routing.param(deparamedCurrentValue);

        this.props.onFilterUpdate(paramedNewValue);
    };
}

// @ts-expect-error
const connector = connect<Props, OwnProps, _, _, _, _>(mapStateToProps, mapDispatchToProps);

export const ActivityFilters = connector(ActivityFiltersComponent);
